import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { Link } from "react-router-dom";
import { 
  X, 
  Menu, 
  LayoutDashboard, 
  User, 
  BookOpen, 
  History, 
  HelpCircle, 
  Users, 
  LogOut,
  ChevronRight,
  SlidersHorizontal,
  BarChart3,
  BookA,
  Gift,
  Layers,
  CreditCard
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { trackClick } from "../utils/analytics";
import StreamSwitcher from "../components/StreamSwitcher";

interface SidebarProps {
  activeItem:
    | "overview"
    | "streams"
    | "profile"
    | "instructions"
    | "transcript-history"
    | "audience"
    | "glossary"
    | "questions"
    | "networking"
    | "advanced-settings"
    | "referrals"
    | "billing";
}

const Sidebar: React.FC<SidebarProps> = ({ activeItem }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const closeButtonRef = useRef<HTMLDivElement>(null);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActive = (item: string) => {
    const currentPath = location.pathname === "/" ? "overview" : location.pathname.substring(1);
    return currentPath === item;
  };

  const handleLogout = async () => {
    const auth = getAuth();
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  useEffect(() => {
    if (isMobileMenuOpen) {
      closeButtonRef.current?.focus();
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") toggleMobileMenu();
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isMobileMenuOpen, toggleMobileMenu]);

  const NavItem = ({ to, icon: Icon, label, itemKey }: { to: string; icon: any; label: string; itemKey: string }) => {
    const active = isActive(itemKey);
    return (
      <Link
        to={to}
        onClick={() => isMobileMenuOpen && toggleMobileMenu()}
        className={`group flex items-center justify-between px-4 py-3 rounded-2xl transition-all duration-200 ${
          active 
          ? "bg-blue-600 text-white shadow-lg shadow-blue-200" 
          : "text-slate-600 hover:bg-blue-50 hover:text-blue-600"
        }`}
      >
        <div className="flex items-center gap-3">
          <Icon size={20} className={active ? "text-white" : "text-slate-400 group-hover:text-blue-500"} />
          <span className="font-medium">{label}</span>
        </div>
        {active && <ChevronRight size={16} className="text-white/70" />}
      </Link>
    );
  };

  return (
    <>
      <button
        onClick={toggleMobileMenu}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-xl shadow-md text-slate-600 hover:text-blue-600 transition-colors"
        aria-label={t("navigation.openMenu")}
      >
        <Menu size={24} />
      </button>

      <aside
        id="sidebar-menu"
        ref={sidebarRef}
        className={`bg-white z-50 fixed inset-y-0 left-0 w-72 border-r border-slate-100 transition-transform duration-300 ease-in-out transform ${
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 lg:static lg:w-72 flex flex-col max-lg:h-[100dvh]`}
      >
        {/* The menu scrolls when it is taller than the screen; Logout below it never moves. */}
        <div className="flex-1 min-h-0 overflow-y-auto">
        {activeItem !== "profile" && activeItem !== "referrals" && <div className="pt-8"><StreamSwitcher /></div>}
        <div className="p-8">
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6">
            {t("navigation.general")}
          </h2>
          <nav className="space-y-2">
            <NavItem to="/" icon={LayoutDashboard} label={t("navigation.overview")} itemKey="overview" />
            <NavItem to="/streams" icon={Layers} label={t("navigation.streams", "Streams")} itemKey="streams" />
            <NavItem to="/profile" icon={User} label={t("navigation.profile")} itemKey="profile" />
            <NavItem to="/instructions" icon={BookOpen} label={t("navigation.instructions")} itemKey="instructions" />
            <NavItem to="/transcript-history" icon={History} label={t("navigation.transcriptHistory")} itemKey="transcript-history" />
            <NavItem to="/audience" icon={BarChart3} label={t("navigation.audience", "Audience")} itemKey="audience" />
            <NavItem to="/glossary" icon={BookA} label={t("navigation.glossary", "Glossary")} itemKey="glossary" />
            <NavItem to="/questions" icon={HelpCircle} label={t("navigation.questions")} itemKey="questions" />
            <NavItem to="/networking" icon={Users} label={t("navigation.networking")} itemKey="networking" />
            <NavItem to="/advanced-settings" icon={SlidersHorizontal} label={t("navigation.advancedSettings", "Advanced Settings")} itemKey="advanced-settings" />
            <NavItem to="/referrals" icon={Gift} label={t("navigation.referrals", "Referrals")} itemKey="referrals" />
            <NavItem to="/billing" icon={CreditCard} label={t("navigation.billing", "Billing")} itemKey="billing" />
          </nav>
        </div>
        </div>

        <div className="shrink-0 p-4 lg:p-8 border-t border-slate-50">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-2xl transition-all duration-200 group"
          >
            <LogOut size={20} className="group-hover:text-red-500" />
            <span className="font-medium">Logout</span>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div
            ref={closeButtonRef}
            onClick={toggleMobileMenu}
            className="lg:hidden absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 cursor-pointer"
          >
            <X size={24} />
          </div>
        )}
      </aside>

      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={toggleMobileMenu}
        ></div>
      )}
    </>
  );
};

export default Sidebar;
